import { ensureKustoLanguageLoaded } from '../../../workspace/common/utils/kustoLanguage';

/**
 * The monaco-kusto worker, resolved for a specific model uri.
 *
 * Verified API (`Docs/superpowers/plans/2026-07-08-kusto-NOTES.md` §S0): the
 * worker is a Comlink-style proxy — do NOT introspect its method names, just
 * call them. `setSchemaFromShowSchema(schema, clusterConnectionString,
 * databaseInContextName)` binds the parsed `.show schema as json` result so
 * monaco-kusto's completion/hover/diagnostics providers become schema-aware.
 */
export type KustoWorker = {
  setSchemaFromShowSchema: (schema: unknown, clusterUrl: string, database: string) => Promise<void>;
  /**
   * The two halves of `setSchemaFromShowSchema`, exposed by monaco-kusto's
   * worker (`@kusto/monaco-kusto` `types.d.ts`): normalise the `.show schema`
   * payload, then install the engine schema. The schema feed calls them
   * separately so it can re-check the document's lifecycle between the two —
   * the combined call would still install a schema for a document that was
   * disconnected while the normalisation ran.
   */
  normalizeSchema: (schema: unknown, clusterUrl: string, database: string) => Promise<unknown>;
  setSchema: (schema: unknown) => Promise<void>;
};

type MonacoKustoGlobal = {
  languages: {
    kusto: {
      getKustoWorker: () => Promise<(uri: unknown) => Promise<KustoWorker>>;
    };
  };
  Uri: { parse: (value: string) => unknown };
};

export async function getKustoWorkerForUri(uri: string): Promise<KustoWorker> {
  await ensureKustoLanguageLoaded();
  // The AMD loader defines a global `monaco` once `vs/editor/editor.main` has
  // loaded — the app has no ambient typing for it (Monaco is loaded lazily
  // via AMD, not imported), so this is a deliberate cast.
  const monaco = (window as unknown as { monaco: MonacoKustoGlobal }).monaco;
  const factory = await monaco.languages.kusto.getKustoWorker();
  return factory(monaco.Uri.parse(uri));
}
